import { useTranslation } from 'react-i18next';
import { useAppConfig } from '@/api/useAppConfig';
import { SUPPORTED_CULTURES } from '@/i18n';
import { baseLanguage, flagClass, languageName } from '@/i18n/languages';
import { useChangeLanguage } from '@/i18n/useChangeLanguage';

/** Inline size the Razor markup gave every flag swatch. */
const FLAG_SIZE = { width: '1.2em', height: '0.9em' } as const;

/**
 * The language submenu shared by the profile and guest dropdowns.
 *
 * `.dropdown-multilevel` is opened by CSS hover alone
 * (`_dropdowns.scss:414`), so there is no state and no JS here — only the click
 * that used to be handled by the `data-action="lang"` dispatcher.
 *
 * The old markup sent bare language codes ("en") because the switcher script
 * expected them. Full cultures are sent now: the backend resolves both, and a
 * bare code cannot distinguish two variants of the same language if one is ever
 * configured.
 */
export function LanguageMenu() {
  const { t, i18n } = useTranslation();
  const { data: config } = useAppConfig();
  const changeLanguage = useChangeLanguage();

  const cultures = config?.supportedCultures?.length ? config.supportedCultures : SUPPORTED_CULTURES;
  const current = baseLanguage(i18n.language);

  return (
    <div className="dropdown-multilevel dropdown-multilevel-left">
      {/* Focusable so the `:focus-within` rule in shell.scss can open the
          submenu: without a tab stop on the trigger, nothing inside can ever
          receive focus while the menu is still hidden. */}
      <div
        className="dropdown-item d-flex justify-content-between align-items-center"
        role="button"
        tabIndex={0}
        aria-haspopup="true"
      >
        <span className="d-flex align-items-center">
          <span className={`${flagClass(i18n.language)} me-2`} style={FLAG_SIZE} />
          <span>{t('Language', 'Language')}</span>
        </span>
        <i className="sa sa-chevron-right" />
      </div>
      <div className="dropdown-menu">
        {cultures.map((culture) => (
          <a
            key={culture}
            href={`#?lang=${baseLanguage(culture)}`}
            className={`dropdown-item${baseLanguage(culture) === current ? ' selected' : ''}`}
            onClick={(event) => {
              event.preventDefault();
              void changeLanguage(culture);
            }}
          >
            <span className={`${flagClass(culture)} me-2`} style={FLAG_SIZE} />
            <span>{languageName(culture)}</span>
          </a>
        ))}
      </div>
    </div>
  );
}
